import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type AudioContextSetup,
  cleanupAudioContext,
  createAudioContext,
  createSourceNode,
  getAudioFilePath,
  isValidFrequency,
  loadAudioFile,
  resumeAudioContext,
  setPanning,
  setVolume,
  stopAudioSource,
} from '../utils/audioUtils';
import { calculateGainValue } from '../utils/volumeMapping';

export interface UseAudioTestReturn {
  loadAudio: (frequency: number) => Promise<void>;
  playTone: (volume: number, ear: 'left' | 'right') => Promise<void>;
  stopTone: () => void;
  cleanup: () => Promise<void>;
  isPlaying: boolean;
  error: Error | null;
  currentFrequency: number | null;
  audioContext: AudioContext | null;
  isAudioContextReady: boolean;
}

export const useAudioTest = (): UseAudioTestReturn => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentFrequency, setCurrentFrequency] = useState<number | null>(null);
  const [isAudioContextReady, setIsAudioContextReady] = useState(false);

  const audioSetupRef = useRef<AudioContextSetup | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const currentFrequencyRef = useRef<number | null>(null);

  const initAudioContext = useCallback(async () => {
    if (audioSetupRef.current) {
      if (audioSetupRef.current.context.state === 'suspended') {
        await resumeAudioContext(audioSetupRef.current.context);
      }
      return audioSetupRef.current;
    }

    try {
      const setup = createAudioContext();
      await resumeAudioContext(setup.context);

      audioSetupRef.current = setup;
      setIsAudioContextReady(true);
      setError(null);

      return setup;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to initialize audio context');
      setError(error);
      throw error;
    }
  }, []);

  const loadAudio = useCallback(
    async (frequency: number) => {
      if (!isValidFrequency(frequency)) {
        throw new Error(`Invalid frequency: ${frequency}`);
      }

      setError(null);

      try {
        const audioSetup = await initAudioContext();
        const audioPath = getAudioFilePath(frequency);
        audioBufferRef.current = await loadAudioFile(audioSetup.context, audioPath);
        currentFrequencyRef.current = frequency;
        setCurrentFrequency(frequency);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(`Failed to load audio for ${frequency}Hz`);
        setError(error);
        throw error;
      }
    },
    [initAudioContext]
  );

  const stopTone = useCallback(() => {
    if (sourceNodeRef.current) {
      stopAudioSource(sourceNodeRef.current);
      sourceNodeRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const cleanup = useCallback(async () => {
    stopTone();
    await cleanupAudioContext(audioSetupRef.current);
    audioSetupRef.current = null;
    audioBufferRef.current = null;
    currentFrequencyRef.current = null;
    setCurrentFrequency(null);
    setIsAudioContextReady(false);
    setError(null);
  }, [stopTone]);

  const playTone = useCallback(
    async (volume: number, ear: 'left' | 'right') => {
      const freq = currentFrequencyRef.current;
      if (!audioSetupRef.current || !audioBufferRef.current || !freq) {
        throw new Error('Audio not ready. Call loadAudio() first.');
      }

      stopTone();

      try {
        const { context, gainNode, pannerNode } = audioSetupRef.current;

        await resumeAudioContext(context);

        const sourceNode = createSourceNode(context, audioBufferRef.current, true);
        sourceNodeRef.current = sourceNode;

        const gainValue = calculateGainValue(volume, freq);
        setVolume(gainNode, gainValue);

        setPanning(pannerNode, ear);

        sourceNode.connect(gainNode);
        sourceNode.start();
        setIsPlaying(true);
        setError(null);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to play audio');
        setError(error);
        throw error;
      }
    },
    [stopTone]
  );

  useEffect(() => {
    return () => {
      stopTone();
      if (audioSetupRef.current) {
        cleanupAudioContext(audioSetupRef.current).catch(() => {
          // Ignore errors on cleanup
        });
      }
    };
  }, [stopTone]);

  return {
    loadAudio,
    playTone,
    stopTone,
    cleanup,
    isPlaying,
    error,
    currentFrequency,
    audioContext: audioSetupRef.current?.context ?? null,
    isAudioContextReady,
  };
};
